<template>
  <div>
    <b-row class="mb-2">
      <b-button
        variant="success"
        class="mr-2 ml-3"
        @click="exportWarrantiesXLS()"
      >
        <span v-label-key="'EXPORT_WARRANTIES_XLSX'"
          >Export Warranties to XLSX</span
        >
      </b-button>

      <b-button variant="success" @click="exportAllClaimsXLS()">
        <span v-label-key="'EXPORT_WARRANTY_CLAIMS_XLSX'"
          >Export Claims to XLSX</span
        >
      </b-button>
    </b-row>
    <b-row>
      <b-col md="9">
        <b-table
          :items="warranties"
          :fields="warrantyFields"
          :busy="loading"
          show-empty
          striped
          hover
          responsive
          sort-by="issue_date"
        >
          <template v-slot:cell(customernumber)="data">
            {{ data.item.customer_number }}
          </template>
          <template v-slot:cell(imei)="data">
            <span v-if="auth.user.selectedAccess.roleLevel > 3">{{
              data.item.imei
            }}</span>
            <p v-else>
              <span v-if="data.item.imei == null" />
              <span v-else-if="data.item.imei.length > 8">{{
                data.item.imei.slice(-8).padStart(data.item.imei.length, '*')
              }}</span>
              <span v-else>{{ data.item.imei }}</span>
            </p>
          </template>
          <template v-slot:cell(claims)="data">
            <b-button
              :disabled="!data.item.claim_count"
              class="mr-1"
              @click="claim_warranty(data.item)"
            >
              <span v-label-key="'SHOW'">Show</span>
            </b-button>
          </template>
          <template v-slot:cell(invoicephoto)="data">
            <a
              v-if="data.item.invoice_photo"
              :href="'https://cdn.gokaizen.com/' + data.item.invoice_photo"
            >
              https://cdn.gokaizen.com/{{ data.item.invoice_photo }}</a
            >
            <span v-else v-label-key="'NO_WARRANTY_INVOICE_AVAILABLE'"
              >No invoice available</span
            >
          </template>
          <template v-slot:table-busy>
            <loading-spinner />
          </template>
        </b-table>
        <b-modal
          ref="claimModal"
          :title="getLabel('WARRANTY_CLAIMS')"
          ok-only
          size="lg"
        >
          <div>
            <b-table
              :items="claims"
              :fields="warrantyClaimsFields"
              :busy="loading"
              show-empty
              striped
              hover
              responsive
            >
              <template v-slot:cell(time)="data">
                {{
                  data.item.request_date
                    .split('')
                    .slice(11, 23)
                    .join('')
                }}
              </template>
              <template v-slot:cell(customernumber)="data">
                {{ data.item.customer_number }}
              </template>
              <template v-slot:cell(imei)="data">
                <span v-if="auth.user.selectedAccess.roleLevel > 3">{{
                  data.item.imei
                }}</span>
                <p v-else>
                  <span v-if="data.item.imei == null" />
                  <span v-else-if="data.item.imei.length > 8">{{
                    data.item.imei
                      .slice(-8)
                      .padStart(data.item.imei.length, '*')
                  }}</span>
                  <span v-else>{{ data.item.imei }}</span>
                </p>
              </template>
              <template v-slot:cell(redeemdate)="data">
                <span v-if="data.item.redeemed_date">
                  {{
                    data.item.redeemed_date
                      .split('')
                      .slice(0, 10)
                      .join('')
                  }}</span
                >
                <span v-else />
              </template>
              <template v-slot:cell(redeemtime)="data">
                <span v-if="data.item.redeemed_date">
                  {{
                    data.item.redeemed_date
                      .split('')
                      .slice(11, 23)
                      .join('')
                  }}</span
                >
                <span v-else />
              </template>

              <template v-slot:table-busy>
                <loading-spinner />
              </template>
            </b-table>
          </div>
          <div v-if="claims" slot="modal-footer">
            <b-button
              v-if="claims"
              variant="primary"
              @click="exportModalClaimXLS()"
            >
              <span v-label-key="'EXPORT_XLSX'">Export XLSX</span>
            </b-button>
          </div>
        </b-modal>
      </b-col>
      <b-col md="3">
        <b-card no-body>
          <b-card-header
            ><span v-label-key="'WARRANTIES_SHOW_FROM_DATE_RANGE'"
              >Show Warranties From</span
            ></b-card-header
          >
          <b-card-body>
            <date-range-selector
              v-model="selectedRange"
              @input="loadWarranties"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import $api from '@/services/api';
import DateRangeSelector from '@/components/DateRangeSelector';
import $auth from '@/services/auth';
import LoadingSpinner from '@/components/LoadingSpinner';
import { mapGetters } from 'vuex';

const fieldIssueDate = {
  key: 'issue_date',
  label: 'Issue Date',
  labelKey: 'WARRANTY_FIELD_ISSUE_DATE',
  sortable: true,
  formatter: value =>
    value
      .split('')
      .slice(0, 10)
      .join(''),
};
const fieldIssueTime = {
  key: 'created',
  label: 'Time',
  labelKey: 'WARRANTY_FIELD_ISSUE_TIME',
  sortable: true,
  formatter: value =>
    value
      .split('')
      .slice(11, 23)
      .join(''),
};
const fieldClaimDate = {
  key: 'request_date',
  label: 'Claim Date',
  labelKey: 'WARRANTY_FIELD_CLAIM_DATE',
  sortable: true,
  formatter: value =>
    value
      .split('')
      .slice(0, 10)
      .join(''),
};

const fieldClaimTime = {
  key: 'time',
  label: 'Claim Time',
  labelKey: 'WARRANTY_FIELD_CLAIM_TIME',
};
const fieldRedeemDate = {
  key: 'redeemdate',
  label: 'Redeemed Date',
  labelKey: 'WARRANTY_FIELD_REDEEMED_DATE',
};

const fieldRedeemTime = {
  key: 'redeemtime',
  label: 'Redeemed Time',
  labelKey: 'WARRANTY_FIELD_REDEEMED_TIME',
};
const fieldWarrantyID = {
  key: 'id',
  label: 'Warranty ID',
  labelKey: 'WARRANTY_FIELD_ID',
  sortable: true,
};
const fieldClaimCode = {
  key: 'claim_code',
  label: 'Claim Code',
  labelKey: 'WARRANTY_FIELD_CLAIM_CODE',
  sortable: true,
};
const fieldStoreName = {
  key: 'store_name',
  label: 'Store Name',
  labelKey: 'WARRANTY_FIELD_STORE_NAME',
  sortable: true,
};
const fieldStoreID = {
  key: 'store_id',
  label: 'Store ID',
  labelKey: 'WARRANTY_FIELD_STORE_ID',
  sortable: true,
};
const fieldCustomerNumber = {
  key: 'customernumber',
  label: 'Customer Number',
  labelKey: 'WARRANTY_FIELD_CUSTOMER_NUMBER',
  sortable: true,
  requiresAdmin: true,
};
const fieldCustomerIMEI = {
  key: 'imei',
  label: 'IMEI/Serial Number',
  labelKey: 'WARRANTY_FIELD_CUSTOMER_IMEI',
  sortable: true,
};
const fieldClaims = {
  key: 'claims',
  label: 'Claims',
  labelKey: 'WARRANTY_FIELD_CLAIMS',
};
const fieldDeviceBrand = {
  key: 'manufacturer_name',
  label: 'Device Brand',
  labelKey: 'WARRANTY_FIELD_DEVICE_BRAND',
  sortable: true,
};
const fieldDeviceName = {
  key: 'device_name',
  label: 'Device Model',
  labelKey: 'WARRANTY_FIELD_DEVICE_MODEL',
  sortable: true,
};
const fieldTemplateName = {
  key: 'template_name',
  label: 'Template',
  labelKey: 'TEMPLATE',
  sortable: true,
};
const fieldFilmtypeName = {
  key: 'filmtype_name',
  label: 'Filmtype Name',
  labelKey: 'WARRANTY_FIELD_FILMTYPE_NAME',
};
const fieldFilmtypeDescription = {
  key: 'filmtype_description',
  label: 'Filmtype Description',
  labelKey: 'WARRANTY_FIELD_FILMTYPE_DESCRIPTION',
};
const fieldFilmcodeID = {
  key: 'filmcode_id',
  label: 'Filmcode ID',
  labelKey: 'WARRANTY_FIELD_FILMCODE_ID',
};
const fieldWarrantyStatus = {
  key: 'status',
  label: 'Status',
  labelKey: 'WARRANTY_FIELD_STATUS',
};
const fieldWarrantyRegistrationDate = {
  key: 'registration_date',
  label: 'Registration Date',
  labelKey: 'WARRANTY_FIELD_REGISTRATION_DATE',
};
const fieldCustomerInvoice = {
  key: 'invoice',
  label: 'Invoice',
  labelKey: 'WARRANTY_FIELD_INVOICE',
};
const fieldCustomerInvoicePhoto = {
  key: 'invoicephoto',
  label: 'Invoice Photo',
  labelKey: 'WARRANTY_FIELD_INVOICE_PHOTO',
};
const fieldWarrantyClaimCount = {
  key: 'claim_count',
  label: 'Claim Count',
  labelKey: 'WARRANTY_FIELD_CLAIM_COUNT',
};
const fieldWarrantyClaimsRedeemed = {
  key: 'claim_redeemed_count',
  label: 'Claims Redeemed',
  labelKey: 'WARRANTY_FIELD_CLAIMS_REDEEMED',
};
const fieldWarrantyFunding = {
  key: 'funding',
  label: 'Funding',
  labelKey: 'WARRANTY_FIELD_FUNDING',
  sortable: true,
};

const claimsFields = [
  fieldClaimDate,
  fieldClaimTime,
  fieldClaimCode,
  fieldWarrantyID,
  fieldStoreName,
  fieldStoreID,
  fieldCustomerIMEI,
  fieldDeviceBrand,
  fieldDeviceName,
  fieldTemplateName,
  fieldFilmtypeName,
  fieldFilmtypeDescription,
  fieldFilmcodeID,
  fieldWarrantyFunding,
  fieldRedeemDate,
  fieldRedeemTime,
];

export default {
  /* eslint-disable-next-line vue/prop-name-casing, vue/require-prop-types */
  components: {
    DateRangeSelector,
    LoadingSpinner,
  },
  props: ['chain_id', 'distributor_id', 'subdistributor_id'],
  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
    }),
    warrantyFields() {
      const fields = [
        fieldIssueDate,
        fieldIssueTime,
        fieldWarrantyID,
        fieldStoreName,
        fieldStoreID,
        fieldCustomerNumber,
        fieldCustomerIMEI,
        fieldClaims,
        fieldDeviceBrand,
        fieldDeviceName,
        fieldTemplateName,
        fieldFilmtypeName,
        fieldFilmtypeDescription,
        fieldFilmcodeID,
        fieldWarrantyStatus,
        fieldWarrantyRegistrationDate,
        fieldCustomerInvoice,
        fieldCustomerInvoicePhoto,
        fieldWarrantyClaimCount,
        fieldWarrantyClaimsRedeemed,
      ];
      fields.forEach(field => {
        if (field.labelKey) {
          field.label = this.getLabel(field.labelKey) || field.label;
        }
      });
      if (this.auth.user.selectedAccess.roleLevel < 4) {
        return fields.filter(field => !field.requiresAdmin);
      }
      return fields;
    },
    warrantyClaimsFields() {
      const fields = [
        fieldClaimDate,
        fieldClaimTime,
        fieldWarrantyID,
        fieldClaimCode,
        fieldStoreName,
        fieldStoreID,
        fieldCustomerIMEI,
        fieldCustomerNumber,
        fieldDeviceBrand,
        fieldDeviceName,
        fieldTemplateName,
        fieldFilmcodeID,
        fieldWarrantyFunding,
        fieldFilmtypeName,
        fieldFilmtypeDescription,
        fieldRedeemDate,
        fieldRedeemTime,
      ];
      fields.forEach(field => {
        if (field.labelKey) {
          field.label = this.getLabel(field.labelKey) || field.label;
        }
      });
      if (this.auth.user.selectedAccess.roleLevel < 4) {
        return fields.filter(field => !field.requiresAdmin);
      }
      return fields;
    },
  },
  data() {
    return {
      loading: false,
      selectedRange: null,
      warranties: null,
      claims: null,
      allClaims: null,
      auth: $auth,
    };
  },
  methods: {
    claim_warranty(warranty) {
      this.$refs.claimModal.show();
      this.loading = true;
      $api.get(`/v1/dashboard/warranties/${warranty.id}/claims`).then(claim => {
        this.claims = claim;
        this.loading = false;
      });
    },
    loadWarranties() {
      this.loading = true;
      const payload = {
        distributor_id: this.distributor_id,
        subdistributor_id: this.subdistributor_id,
        chain_id: this.chain_id,
      };
      payload._start = this.selectedRange.start; // eslint-disable-line no-underscore-dangle
      payload._end = this.selectedRange.end; // eslint-disable-line no-underscore-dangle
      $api
        .get('/v1/dashboard/warranties', payload)
        .then(warranty => {
          this.warranties = warranty;
          this.loading = false;
        })
        .catch(error => {
          console.error(error);
          this.loading = false;
        });
      $api
        .get('/v1/dashboard/warrantyclaims', payload)
        .then(warranty => {
          this.allClaims = warranty;
        })
        .catch(error => {
          console.error(error);
        });
    },

    exportModalClaimXLS() {
      /* eslint-disable */
      const wb = XLSX.utils.book_new();
      const headers = [
        ...claimsFields,
        this.auth.user.selectedAccess.roleLevel > 3
          ? fieldCustomerNumber
          : null,
      ].map(field => {
        if (field.labelKey) {
          return this.getLabel(field.labelKey);
        }
        if (field.label) {
          return field.label;
        }
        return;
      });
      const dataArr = [];
      let newData = this.claims.sort(function(a, b) {
        return a.request_date.localeCompare(b.request_date);
      });
      newData.forEach(claim => {
        dataArr.push([
          claim.request_date
            .split('')
            .slice(0, 10)
            .join(''),
          claim.request_date
            .split('')
            .slice(11, 23)
            .join(''),
          claim.claim_code,
          claim.id,
          claim.store_name,
          claim.store_id,
          this.auth.user.selectedAccess.roleLevel > 3
            ? claim.imei
            : claim.imei == null
            ? null
            : claim.imei.length > 8
            ? claim.imei.slice(-8).padStart(claim.imei.length, '*')
            : claim.imei,
          claim.device_brand,
          claim.device_model,
          claim.templates_name,
          claim.filmtype_name,
          claim.filmtype_description,
          claim.filmcode_id,
          claim.funding,
          claim.redeemed_date == null
            ? null
            : claim.request_date
                .split('')
                .slice(0, 10)
                .join(''),
          claim.redeemed_date == null
            ? null
            : claim.request_date
                .split('')
                .slice(11, 23)
                .join(''),
          this.auth.user.selectedAccess.roleLevel > 3
            ? claim.customer_number
            : null,
        ]);
      });
      let sortedData = [headers, ...dataArr];
      const ws = XLSX.utils.aoa_to_sheet(sortedData);
      XLSX.utils.book_append_sheet(wb, ws, 'Claims');
      XLSX.writeFile(wb, 'claims.xlsx');
    },
    exportAllClaimsXLS() {
      /* eslint-disable */
      const wb = XLSX.utils.book_new();
      const headers = [
        ...claimsFields,
        this.auth.user.selectedAccess.roleLevel > 3
          ? fieldCustomerNumber
          : null,
      ].map(field => {
        if (field.labelKey) {
          return this.getLabel(field.labelKey);
        }
        if (field.label) {
          return field.label;
        }
        return;
      });
      const dataArr = [];
      let newData = this.allClaims.sort(function(a, b) {
        return a.request_date.localeCompare(b.request_date);
      });
      newData.forEach(claim => {
        dataArr.push([
          claim.request_date
            .split('')
            .slice(0, 10)
            .join(''),
          claim.request_date
            .split('')
            .slice(11, 23)
            .join(''),
          claim.claim_code,
          claim.id,
          claim.store_name,
          claim.store_id,
          this.auth.user.selectedAccess.roleLevel > 3
            ? claim.imei
            : claim.imei == null
            ? null
            : claim.imei.length > 8
            ? claim.imei.slice(-8).padStart(claim.imei.length, '*')
            : claim.imei,
          claim.device_brand,
          claim.device_model,
          claim.templates_name,
          claim.filmtype_name,
          claim.filmtype_description,
          claim.filmcode_id,
          claim.funding,
          claim.redeemed_date == null
            ? null
            : claim.request_date
                .split('')
                .slice(0, 10)
                .join(''),
          claim.redeemed_date == null
            ? null
            : claim.request_date
                .split('')
                .slice(11, 23)
                .join(''),
          this.auth.user.selectedAccess.roleLevel > 3
            ? claim.customer_number
            : null,
        ]);
      });

      let sortedData = [headers, ...dataArr];
      const ws = XLSX.utils.aoa_to_sheet(sortedData);
      XLSX.utils.book_append_sheet(wb, ws, 'AllClaims');
      XLSX.writeFile(wb, 'AllClaims.xlsx');
    },
    exportWarrantiesXLS() {
      /* eslint-disable */
      const wb = XLSX.utils.book_new();
      const headers = [
        fieldIssueDate,
        fieldIssueTime,
        fieldWarrantyID,
        fieldStoreName,
        fieldStoreID,
        fieldCustomerIMEI,
        fieldDeviceBrand,
        fieldDeviceName,
        fieldTemplateName,
        fieldFilmtypeName,
        fieldFilmtypeDescription,
        fieldFilmcodeID,
        fieldWarrantyStatus,
        fieldWarrantyRegistrationDate,
        fieldCustomerInvoice,
        fieldCustomerInvoicePhoto,
        fieldWarrantyClaimCount,
        fieldWarrantyClaimsRedeemed,
        this.auth.user.selectedAccess.roleLevel > 3
          ? fieldCustomerNumber
          : null,
      ].map(field => {
        if (field.labelKey) {
          return this.getLabel(field.labelKey);
        }
        if (field.label) {
          return field.label;
        }
        return;
      });
      const dataArr = [];
      let newData = this.warranties.sort(function(a, b) {
        return a.issue_date.localeCompare(b.issue_date);
      });

      newData.forEach(warranty => {
        dataArr.push([
          warranty.issue_date
            .split('')
            .slice(0, 10)
            .join(''),
          warranty.created
            .split('')
            .slice(11, 23)
            .join(''),
          warranty.id,
          warranty.store_name,
          warranty.store_id,
          this.auth.user.selectedAccess.roleLevel > 3
            ? warranty.imei
            : warranty.imei == null
            ? null
            : warranty.imei.length > 8
            ? warranty.imei.slice(-8).padStart(warranty.imei.length, '*')
            : warranty.imei,
          warranty.manufacturer_name,
          warranty.device_name,
          warranty.template_name,
          warranty.filmtype_name,
          warranty.filmtype_description,
          warranty.filmcode_id,
          warranty.status,
          warranty.registration_date,
          warranty.invoice,
          warranty.invoice_photo == null || !warranty.invoice_photo
            ? null
            : 'https://cdn.gokaizen.com/' + warranty.invoice_photo,
          warranty.claim_count,
          warranty.claim_redeemed_count,
          this.auth.user.selectedAccess.roleLevel > 3
            ? warranty.customer_number
            : null,
        ]);
      });

      let sortedData = [headers, ...dataArr];
      const ws = XLSX.utils.aoa_to_sheet(sortedData);
      XLSX.utils.book_append_sheet(wb, ws, 'Warranties');
      XLSX.writeFile(wb, 'Warranties.xlsx');
    },
  },
};
</script>
